import React, { useState } from 'react';
import { DefinitionOfReady, DefinitionOfReadyCriteria } from '../../../types';
import { ProjectContext } from '../../../clients/ogg';
import { Header, TitleSize } from 'azure-devops-ui/Header';
import { ZeroData, ZeroDataActionType } from 'azure-devops-ui/ZeroData';
import { IListItemDetails, IListRow, ListItem, ListSelection, ScrollableList } from 'azure-devops-ui/List';
import { IHeaderCommandBarItem } from 'azure-devops-ui/HeaderCommandBar';
import { ObservableArray } from 'azure-devops-ui/Core/Observable';
import DefinitionOfReadyCriteriaEditor from './DefinitionOfReadyCriteriaEditor';
import { clone } from 'lodash';
import { useProjectContextProviderContext } from '../../../hooks/useProjectContextProviderContext';
import { DefaultDefinitionOfReady } from './defaultDefinitionOfReady';
import DefinitionOfReadyCriteriaView from './DefinitionOfReadyCriteriaView';

export interface IDefinitionOfReadyProps {
  dor: DefinitionOfReady;
  projectContext: ProjectContext;
  onDorUpdated?: (dor: DefinitionOfReady) => void;
}

export default function DefinitionOfReady(props: IDefinitionOfReadyProps) {
  const selection = new ListSelection(true);
  const [dor, setDor] = useState(new ObservableArray<DefinitionOfReadyCriteria>(props.dor ?? []));
  const [selectedCriteria, setSelectedCriteria] = useState(null as IListRow<DefinitionOfReadyCriteria>);
  const [refresh, setRefresh] = useState(0);
  const projectContextProvider = useProjectContextProviderContext();
  console.log('loading dor', dor, props.dor);

  const dorButtons: IHeaderCommandBarItem[] = [
    {
      iconProps: {
        iconName: 'Home',
      },
      id: 'useDefaults',
      text: "Use Default",
      important: true,
      onActivate: () => {
        dor.splice(0, dor.length, ...DefaultDefinitionOfReady);

        updateDorSource();
        setRefresh(refresh + 1);
      },
      tooltipProps: { text: 'Use the default Definition Of Ready supplied by Ogg' },
    },
    {
      iconProps: {
        iconName: 'PageAdd',
      },
      id: 'addDor',
      important: true,
      isPrimary: true,
      onActivate: () => {
        addDorCriteria();
      },
      tooltipProps: { text: 'Add a criteria for the Definition Of Ready' },
    },
  ];

  function renderRow(
    index: number,
    item: DefinitionOfReadyCriteria,
    details: IListItemDetails<DefinitionOfReadyCriteria>,
    key?: string
  ): JSX.Element {
    return (
      <ListItem key={key || 'list-item' + index} index={index} details={details}>
        <DefinitionOfReadyCriteriaView criteria={item}/>
      </ListItem>
    );
  }

  function addDorCriteria() {
    setSelectedCriteria({
      data: "",
      index: dor.length,
    });
  }

  function updateDorSource() {
    props.projectContext.definitionOfReady = dor.value;
    projectContextProvider.setProjectContext(props.projectContext);
    if (!!props.onDorUpdated) props.onDorUpdated(dor.value);
    setSelectedCriteria(null);
    setDor(dor);
  }

  function updateCriteria(criteria: DefinitionOfReadyCriteria){
    if (selectedCriteria.index >= dor.length) dor.push(criteria);
    else dor.change(selectedCriteria.index, criteria);

    updateDorSource();
  }

  function deleteCriteria() {
    dor.splice(selectedCriteria.index, 1);

    updateDorSource();
  }

  return (
    <div className='editable-list' style={{ width: '100%' }}>
      <Header
        title={'Definition Of Ready'}
        description='Provide a list of criteria that need to apply for a work item to be considered ready for development in a sprint.'
        commandBarItems={dorButtons}
        titleSize={TitleSize.Small}
        titleIconProps={{ iconName: 'WaitlistConfirm' }}
        titleAriaLevel={3}
      />
      <div
        style={{ display: 'flex', minHeight: '300px', maxWidth: '100%' }}
        className='editable-list-content'
      >
        {dor.length == 0 ? (
          <div className='flex-column flex-center full-width'>
            <ZeroData
              primaryText='Looks empty in here'
              imageAltText='No Definition Of Ready'
              iconProps={{
                iconName: 'WaitlistConfirm',
              }}
              actionText='Add a new criteria for the Definition of Ready'
              actionType={ZeroDataActionType.ctaButton}
              onActionClick={addDorCriteria}
            />
          </div>
        ) : (
          <ScrollableList
            itemProvider={dor}
            renderRow={renderRow}
            selection={selection}
            maxWidth='100%'
            width='100%'
            onSelect={(event, row) => {
              setSelectedCriteria(row);
            }}
          />
        )}
      </div>
      {selectedCriteria != null && (
        <DefinitionOfReadyCriteriaEditor
          criteria={clone(selectedCriteria.data)}
          onCancel={() => setSelectedCriteria(null)}
          onCriteriaChanged={updateCriteria}
          onDelete={deleteCriteria}
        />
      )}
    </div>
  );
}
